import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Heart, CheckCircle2 } from "lucide-react";

const CARE_PRIORITY = [
  { value: "memory_care", label: "Memory care / dementia" },
  { value: "parkinsons", label: "Parkinson's" },
  { value: "mobility", label: "Mobility / stroke recovery" },
  { value: "general", label: "General senior placement" },
];

const TIMELINE = [
  { value: "urgent", label: "Urgent — within 2 weeks" },
  { value: "1_3_months", label: "1–3 months" },
  { value: "exploring", label: "Just exploring options" },
];

export default function PlacementRequestForm({
  defaultContact = {},
  hideContactFields = false,
  onSuccess,
  variant = "card", // "card" | "inline"
}) {
  const [form, setForm] = useState({
    loved_one_name: "",
    situation: "",
    care_priority: "memory_care",
    preferred_area: "",
    timeline: "1_3_months",
    preferred_contact: "phone",
    message: "",
    contact_name: defaultContact.name || "",
    contact_email: defaultContact.email || "",
    contact_phone: defaultContact.phone || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      await api.post("/placement-requests", form);
      setSubmitted(true);
      toast.success("Thank you — a Providence Partners advisor will reach out shortly.");
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not submit — please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const Wrap = variant === "card" ? Card : "div";
  const wrapProps = variant === "card"
    ? { className: "rounded-2xl border border-gold/40 bg-gradient-to-br from-gold/5 to-card p-6 shadow-sm" }
    : { className: "" };

  if (submitted) {
    return (
      <Wrap {...wrapProps}>
        <div className="text-center" data-testid="placement-request-success">
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald" />
          <h3 className="mt-3 font-serif text-2xl text-forest">Your request is in our hands.</h3>
          <p className="mt-2 text-muted-foreground">
            A Providence Partners advisor will reach out within one business day to start the
            conversation — no pressure, no commitment.
          </p>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap {...wrapProps}>
      <form onSubmit={submit} className="space-y-4" data-testid="placement-request-form">
        {referralSource && (
          <input type="hidden" data-testid="placement-referral-source" value={referralSource} readOnly />
        )}
        {!hideContactFields && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Your name</label>
              <Input
                required
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                data-testid="placement-contact-name"
                className="mt-1 h-11"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Email</label>
              <Input
                required
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                data-testid="placement-contact-email"
                className="mt-1 h-11"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Phone</label>
              <Input
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                placeholder="(248) 555-0123"
                data-testid="placement-contact-phone"
                className="mt-1 h-11"
              />
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Loved one's first name</label>
            <Input
              value={form.loved_one_name}
              onChange={(e) => setForm({ ...form, loved_one_name: e.target.value })}
              placeholder="e.g. Margaret"
              data-testid="placement-loved-one"
              className="mt-1 h-11"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Care priority</label>
            <select
              value={form.care_priority}
              onChange={(e) => setForm({ ...form, care_priority: e.target.value })}
              data-testid="placement-care-priority"
              className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
            >
              {CARE_PRIORITY.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Preferred area (city or ZIP)</label>
            <Input
              value={form.preferred_area}
              onChange={(e) => setForm({ ...form, preferred_area: e.target.value })}
              placeholder="e.g. Troy, MI or 48084"
              data-testid="placement-preferred-area"
              className="mt-1 h-11"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Timeline</label>
            <select
              value={form.timeline}
              onChange={(e) => setForm({ ...form, timeline: e.target.value })}
              data-testid="placement-timeline"
              className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
            >
              {TIMELINE.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Current situation</label>
            <Textarea
              rows={3}
              placeholder="A few words about where Mom (or Dad) is now and what's prompting the search…"
              value={form.situation}
              onChange={(e) => setForm({ ...form, situation: e.target.value })}
              data-testid="placement-situation"
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">Anything else you'd like us to know? (optional)</label>
            <Textarea
              rows={2}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="placement-message"
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-muted-foreground">How should we reach you first?</label>
            <div className="mt-2 flex gap-2">
              {[{ v: "phone", label: "Phone call" }, { v: "email", label: "Email" }].map((c) => (
                <button
                  key={c.v}
                  type="button"
                  onClick={() => setForm({ ...form, preferred_contact: c.v })}
                  data-testid={`placement-preferred-contact-${c.v}`}
                  className={`rounded-full px-4 py-2 text-sm transition-colors ${
                    form.preferred_contact === c.v
                      ? "bg-primary text-primary-foreground"
                      : "border border-border bg-card text-foreground hover:bg-muted"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <Button
          type="submit"
          disabled={submitting}
          data-testid="placement-submit-btn"
          className="w-full rounded-full bg-primary py-6 text-base text-primary-foreground"
        >
          <Heart className="mr-2 h-4 w-4" />
          {submitting ? "Sending…" : "Connect me with a Providence Partners advisor"}
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          Your information goes only to the Providence Partners advisor team — never to individual facilities.
        </p>
      </form>
    </Wrap>
  );
}
